import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Chip from '@material-ui/core/Chip';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Link } from 'react-router-dom';

import api from '../../../services';

const useStyles = makeStyles((theme) => ({
  panel: { maxWidth: 680, paddingTop: theme.spacing(1) },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  item: {
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: theme.palette.primary.main,
    marginTop: 6,
    flexShrink: 0,
  },
  dotRead: { background: 'transparent', border: `1px solid ${theme.palette.divider}` },
  content: { flex: 1 },
  typeChip: { fontSize: 11, height: 20 },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: '0.8125rem',
    '&:hover': { textDecoration: 'underline' },
  },
}));

const TYPE_LABELS = {
  quotation_submitted: 'Quotation',
  team_member_registered: 'Team',
  jobs_tomorrow: 'Jobs',
  general: 'Info',
};

const TYPE_COLORS = {
  quotation_submitted: 'primary',
  team_member_registered: 'secondary',
  jobs_tomorrow: 'default',
  general: 'default',
};

const limit = 20;

export default function NotificationsTab() {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);

  const load = useCallback(async (nextSkip = 0) => {
    setLoading(true);
    try {
      const { data } = await api.get('/notifications', { params: { limit, skip: nextSkip } });
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total || 0));
      setSkip(nextSkip);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(0);
  }, [load]);

  const markAllRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch {
      toast.error('Could not mark notifications as read');
    }
  };

  const markRead = async (id) => {
    if (id === 'virtual-jobs-tomorrow') return;
    try {
      await api.patch(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    } catch {
      /* silent */
    }
  };

  const unreadCount = items.filter((n) => !n.isRead).length;

  return (
    <div className={classes.panel}>
      <div className={classes.header}>
        <Box>
          <Typography variant="h6">Notifications</Typography>
          <Typography variant="body2" color="textSecondary">
            Activity from jobs, quotations, and your team.
          </Typography>
        </Box>
        {unreadCount > 0 && (
          <Button size="small" onClick={markAllRead}>
            Mark all as read
          </Button>
        )}
      </div>

      {loading ? (
        <CircularProgress size={28} />
      ) : items.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No notifications yet.
        </Typography>
      ) : (
        <>
          {items.map((row) => (
            <div
              key={row.id}
              className={classes.item}
              onClick={() => markRead(row.id)}
              style={{ cursor: row.isRead ? 'default' : 'pointer' }}
            >
              <span className={`${classes.dot} ${row.isRead ? classes.dotRead : ''}`} />
              <div className={classes.content}>
                <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={0.25}>
                  <Typography variant="subtitle2" style={{ fontWeight: row.isRead ? 400 : 600 }}>
                    {row.title}
                  </Typography>
                  <Chip
                    size="small"
                    label={TYPE_LABELS[row.type] || row.type}
                    color={TYPE_COLORS[row.type] || 'default'}
                    className={classes.typeChip}
                  />
                </Box>
                <Typography variant="body2" color="textSecondary">
                  {row.message}
                </Typography>
                <Box mt={0.5} display="flex" alignItems="center" style={{ gap: 12 }}>
                  <Typography variant="caption" color="textSecondary">
                    {row.createdAt ? moment(row.createdAt).fromNow() : ''}
                  </Typography>
                  {row.link && (
                    <Link to={row.link} className={classes.link} onClick={(e) => e.stopPropagation()}>
                      View →
                    </Link>
                  )}
                </Box>
              </div>
            </div>
          ))}
          <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
            <Button disabled={skip <= 0} onClick={() => load(Math.max(0, skip - limit))}>
              Previous
            </Button>
            <Typography variant="caption" color="textSecondary">
              {skip + 1}–{Math.min(skip + items.length, total)} of {total}
            </Typography>
            <Button disabled={skip + items.length >= total} onClick={() => load(skip + limit)}>
              Next
            </Button>
          </Box>
        </>
      )}
    </div>
  );
}
